<form [formGroup]="pageForm" (ngSubmit)="onSubmit()" (keydown.enter)="$event.preventDefault()"
	class="main-content mb-4 mx-lg-4 mx-md-4 mx-sm-0 px-sm-4 px-3">

	<ms-page-title />

	@if (submissionErrorMessage()) {
		<div class="alert alert-danger" role="alert">
			{{ submissionErrorMessage() }}
		</div>
	}

	<!-- Entry-level Effective Date and Note -->
	<div class="row mb-4">
		<div class="col-lg-3 col-md-4">
			<ms-input-label
				[isRequired]="isFieldRequired(pageForm, 'effectiveDate')"
				[labelText]="'Effective Date'" />
			<input
				type="date"
				id="effectiveDate"
				formControlName="effectiveDate"
				[required]="isFieldRequired(pageForm, 'effectiveDate')"
				class="form-control"
				[class.is-invalid]="pageForm.get('effectiveDate')?.invalid && pageForm.get('effectiveDate')?.touched"
				[class.is-valid]="pageForm.get('effectiveDate')?.valid && pageForm.get('effectiveDate')?.touched" />
			@if (pageForm.get('effectiveDate')?.invalid && pageForm.get('effectiveDate')?.touched) {
				<div class="error-messages">
					@if (pageForm.get('effectiveDate')?.errors?.['required']) {
						<p class="text-danger text-start pt-1 mb-0">{{ FormValidatorMessages.ManualGLEntryEffectiveDateRequired }}</p>
					}
				</div>
			}
		</div>
		<div class="col-lg-9 col-md-8 mt-md-0 mt-sm-2 mt-2">
			<ms-input-label [labelText]="'Note'" />
			<input
				type="text"
				id="note"
				formControlName="note"
				maxlength="500"
				class="form-control"
				placeholder="Optional note for this journal entry" />
		</div>
	</div>

	<!-- Entries (single responsive layout) -->
	<div class="gl-entries">
		<!-- Column headers - desktop only -->
		<div class="gl-header">
			<div class="gl-header-cell gl-required">Account</div>
			<div class="gl-header-cell">Debit / Credit</div>
			<div class="gl-header-cell gl-required">Amount</div>
			<div class="gl-header-cell">Payee</div>
			<div class="gl-header-cell"></div>
		</div>

		<div formArrayName="entries">
			@for (entry of entries.controls; track rowIds[$index]; let i = $index) {
				<div class="gl-entry" [formGroupName]="$index">
					<!-- Account -->
					<div class="gl-field gl-field-account">
						<label class="gl-mobile-label">
							Account<span class="gl-required-dot"></span>
						</label>
						<ms-chart-of-account-typeahead
							formControlName="chartOfAccountsId"
							[organizationId]="userSessionService.activeOrganization()?.organizationId"
							[invalid]="getEntryGroup(i).get('chartOfAccountsId')?.invalid && getEntryGroup(i).get('chartOfAccountsId')?.touched ? true : false" />
						@if (getEntryGroup(i).get('chartOfAccountsId')?.invalid && getEntryGroup(i).get('chartOfAccountsId')?.touched) {
							<p class="text-danger text-start pt-1 mb-0 gl-mobile-error">{{ FormValidatorMessages.ManualGLEntryAccountRequired }}</p>
						}
					</div>

					<!-- Debit / Credit -->
					<div class="gl-field gl-field-dc">
						<label class="gl-mobile-label">Debit / Credit</label>
						<div class="btn-group btn-group-sm w-100" role="group">
							<input type="radio" class="btn-check" [id]="'debit-' + i" [name]="'dc-' + i"
								[checked]="getEntryGroup(i).get('isDebit')?.value === true"
								(change)="getEntryGroup(i).get('isDebit')?.setValue(true)" autocomplete="off" />
							<label class="btn gl-dc-label gl-dc-debit" [for]="'debit-' + i">Debit</label>
							<input type="radio" class="btn-check" [id]="'credit-' + i" [name]="'dc-' + i"
								[checked]="getEntryGroup(i).get('isDebit')?.value === false"
								(change)="getEntryGroup(i).get('isDebit')?.setValue(false)" autocomplete="off" />
							<label class="btn gl-dc-label gl-dc-credit" [for]="'credit-' + i">Credit</label>
						</div>
					</div>

					<!-- Amount -->
					<div class="gl-field gl-field-amount">
						<label class="gl-mobile-label">
							Amount<span class="gl-required-dot"></span>
						</label>
						<ms-amount-input formControlName="amount" />
					</div>

					<!-- Payee -->
					<div class="gl-field gl-field-payee">
						<label class="gl-mobile-label">Payee</label>
						<input
							type="text"
							formControlName="payee"
							class="form-control form-control-sm"
							placeholder="Payee"
							maxlength="250" />
					</div>

					<!-- Remove button -->
					<div class="gl-field gl-field-action">
						<button type="button"
							class="btn btn-link p-0 gl-remove-btn"
							[class.text-danger]="entries.length > 1"
							[class.text-muted]="entries.length <= 1"
							[disabled]="entries.length <= 1"
							(click)="removeRow(i)"
							title="Remove row">
							<fa-duotone-icon icon="trash" secondaryOpacity="0.9" />
						</button>
					</div>
				</div>
			}
		</div>
	</div>

	<!-- Add Row Button -->
	<div class="mt-3 mb-4">
		<button type="button" class="btn btn-ms-secondary" (click)="addRow()">
			<fa-duotone-icon icon="plus" secondaryOpacity="0.9" class="me-1" />
			Add Row
		</button>
	</div>

	<!-- Totals -->
	<div class="row mb-4">
		<div class="col-12">
			<div class="d-flex flex-wrap gap-4 align-items-center justify-content-end p-3 rounded">
				<div>
					<span class="text-muted">Total Debits:</span>
					<span class="fw-medium ms-1">{{ formatCurrency(totalDebits()) }}</span>
				</div>
				<div>
					<span class="text-muted">Total Credits:</span>
					<span class="fw-medium ms-1">{{ formatCurrency(totalCredits()) }}</span>
				</div>
				<div>
					<span class="text-muted">Difference:</span>
					<span class="fw-medium ms-1" [class.text-danger]="difference() > 0.001"
						[class.text-success]="difference() < 0.001 && totalDebits() > 0">
						{{ formatCurrency(difference()) }}
					</span>
				</div>
			</div>
		</div>
	</div>

	<!-- Form Action Buttons -->
	<div class="d-flex flex-column flex-sm-row justify-content-end gap-2 mt-3 align-items-stretch align-items-sm-center">
		<button type="button" class="btn btn-ms-secondary order-2 order-sm-1" (click)="onCancel()">
			Cancel
		</button>
		<button type="submit" class="btn btn-primary order-1 order-sm-2">
			@if (isSubmitting) {
				<div class="spinner-border text-light spinner-border-sm" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			} @else {
				Submit
			}
		</button>
	</div>
</form>

<ms-confirmation-modal #glConfirmationModalRef modalId="glConfirmationModal"
	confirmButtonText="Leave" cancelButtonText="Stay"
	(confirmed)="confirmationConfirmFunction()"
	(cancelled)="confirmationCancelFunction()"
/>
